import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosProgressEvent } from "axios";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    IBulkCreateClaim,
    IBulkCreateClaimResponse,
    IBulkUpdateClaims,
    IChangeClaimsProvider,
    IChangeClaimVoidStatus,
    IChangeInsurance,
    IClaim,
    IClaimActivity,
    ICreateBulkParentClaimResponse,
    ICreateDraft,
    ICreateDraftResponse,
    IDeleteClaimAttachment,
    IDeleteClaimsAttachmentsResponse,
    ISplitClaimsToNewClaimResponse,
    IUpdateBulkParentClaimResponse,
    IUpdateClaimContent,
    IUpdateClaimsActivity,
    IUpdateClaimStatus,
} from "../types/claims.types";
import {
    addNotesToBulkClaim,
    bulkCreateClaims,
    bulkUpdateClaims,
    changeClaimsProvider,
    changeClaimVoidStatus,
    changeInsuranceProvider,
    checkClaimStatus,
    createBulkParentClaim,
    createDraftClaim,
    createManualClaim,
    deleteClaims,
    deleteClaimsAttachments,
    editManualClaim,
    removeClaimFromBulkClaim,
    removeClaimsFromBulkClaim,
    splitClaimsToNewClaim,
    updateClaimContent,
    updateClaimsActivity,
    updateClaimStatus,
} from "../services/claims.services";

export function useUpdateClaimStatus() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<IUpdateClaimStatus>,
        AxiosError<IApiResponse<IUpdateClaimStatus>>,
        IUpdateClaimStatus
    >({
        mutationKey: ["update_claim_status"],
        mutationFn: (payload: IUpdateClaimStatus) => updateClaimStatus(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["parent-claim"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["get-claim"],
            });
        },
    });
}

export function useCreateDraftClaim() {
    return useMutation<
        IApiResponse<ICreateDraftResponse>,
        AxiosError<IApiResponse<ICreateDraftResponse>>,
        ICreateDraft
    >({
        mutationKey: ["create_draft_claim"],
        mutationFn: (payload: ICreateDraft) => createDraftClaim(payload),
    });
}

export function useBulkCreateClaim() {
    return useMutation<
        IApiResponse<IBulkCreateClaimResponse>,
        AxiosError<IApiResponse<IBulkCreateClaimResponse>>,
        IBulkCreateClaim
    >({
        mutationKey: ["bulk_create_claim"],
        mutationFn: (payload: IBulkCreateClaim) => bulkCreateClaims(payload),
    });
}

export function useCreateBulkParentClaim() {
    return useMutation<
        IApiResponse<ICreateBulkParentClaimResponse>,
        AxiosError<IApiResponse<ICreateBulkParentClaimResponse>>,
        IBulkCreateClaim
    >({
        mutationKey: ["create_bulk_parent_claim"],
        mutationFn: (payload: IBulkCreateClaim) =>
            createBulkParentClaim(payload),
    });
}

export function useRemoveClaimFromBulkClaim() {
    return useMutation<
        IApiResponse<IUpdateBulkParentClaimResponse>,
        AxiosError<IApiResponse<IUpdateBulkParentClaimResponse>>,
        { parentClaimId: string; claimId: string }
    >({
        mutationKey: ["remove-claim-from-bulk-claim"],
        mutationFn: ({ parentClaimId, claimId }) =>
            removeClaimFromBulkClaim(parentClaimId, claimId),
    });
}

export function useRemoveClaimsFromBulkClaim() {
    return useMutation<
        IApiResponse<IUpdateBulkParentClaimResponse>,
        AxiosError<IApiResponse<IUpdateBulkParentClaimResponse>>,
        { parentClaimId: string; claimIds: string[] }
    >({
        mutationKey: ["remove-claims-from-bulk-claim"],
        mutationFn: ({ parentClaimId, claimIds }) =>
            removeClaimsFromBulkClaim(parentClaimId, claimIds),
    });
}

export function useSplitClaimsToNewClaim() {
    return useMutation<
        IApiResponse<ISplitClaimsToNewClaimResponse>,
        AxiosError<IApiResponse<ISplitClaimsToNewClaimResponse>>,
        { parentClaimId: string; claimIds: string[] }
    >({
        mutationKey: ["split-claims-to-new-claim"],
        mutationFn: ({ parentClaimId, claimIds }) =>
            splitClaimsToNewClaim(parentClaimId, claimIds),
    });
}

export function useAddNotesToBulkClaim() {
    return useMutation<
        IApiResponse<IUpdateBulkParentClaimResponse>,
        AxiosError<IApiResponse<IUpdateBulkParentClaimResponse>>,
        { parentClaimId: string; noteIds: string[] }
    >({
        mutationKey: ["add-notes-to-bulk-claim"],
        mutationFn: ({ parentClaimId, noteIds }) =>
            addNotesToBulkClaim(parentClaimId, noteIds),
    });
}

export function useBulkUpdateClaims() {
    return useMutation<
        IApiResponse<IBulkUpdateClaims>,
        AxiosError<IApiResponse<IBulkUpdateClaims>>,
        IBulkUpdateClaims
    >({
        mutationKey: ["bulk-update-claims"],
        mutationFn: (payload: IBulkUpdateClaims) => bulkUpdateClaims(payload),
    });
}

export function useDeleteClaims() {
    interface IDeleteClaimsResponse {
        deleted_claim_ids: string[];
        undeleted_claim_ids: string[];
    }

    return useMutation<
        IApiResponse<IDeleteClaimsResponse>,
        AxiosError<IApiResponse<IDeleteClaimsResponse>>,
        string[]
    >({
        mutationKey: ["delete-claims"],
        mutationFn: (claimIds: string[]) => deleteClaims(claimIds),
    });
}

export function useDeleteClaimsAttachments() {
    return useMutation<
        IApiResponse<IDeleteClaimsAttachmentsResponse>,
        AxiosError<IApiResponse<IDeleteClaimsAttachmentsResponse>>,
        IDeleteClaimAttachment
    >({
        mutationKey: ["delete-claims-attachments"],
        mutationFn: (payload: IDeleteClaimAttachment) =>
            deleteClaimsAttachments(payload),
    });
}

export function useCheckClaimStatus() {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        string[]
    >({
        mutationKey: ["check-claim-status"],
        mutationFn: (claimIds: string[]) => checkClaimStatus(claimIds),
    });
}

export function useUpdateClaimContent() {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IUpdateClaimContent
    >({
        mutationKey: ["update-claim-content"],
        mutationFn: (payload) => updateClaimContent(payload),
    });
}

export function useCreateManualClaim() {
    const [uploadProgress, setUploadProgress] = React.useState(0);
    interface ICreateClaimResponse
        extends IApiResponse<{
            invoice_id: string;
            note_id: string;
            claim: IClaim;
        }> {
        claim_id: string;
    }

    const mutation = useMutation<
        ICreateClaimResponse,
        AxiosError<ICreateClaimResponse>,
        FormData
    >({
        mutationKey: ["create-manual-claim"],
        mutationFn: (payload) =>
            createManualClaim(payload, (progressEvent: AxiosProgressEvent) => {
                if (progressEvent.total) {
                    setUploadProgress(
                        Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        )
                    );
                }
            }),
        onSuccess: () => {
            setUploadProgress(0);
        },
        onError: () => {
            setUploadProgress(0);
        },
    });

    return { ...mutation, uploadProgress };
}

export function useEditManualClaim(claimId: string) {
    const [uploadProgress, setUploadProgress] = React.useState(0);
    interface IEditClaimResponse extends IApiResponse<{ claim: IClaim }> {
        claim_id: string;
    }

    const mutation = useMutation<
        IEditClaimResponse,
        AxiosError<IEditClaimResponse>,
        FormData
    >({
        mutationKey: ["edit-manual-claim"],
        mutationFn: (payload) =>
            editManualClaim(
                claimId,
                payload,
                (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total) {
                        setUploadProgress(
                            Math.round(
                                (progressEvent.loaded * 100) /
                                    progressEvent.total
                            )
                        );
                    }
                }
            ),
        onSuccess: () => {
            setUploadProgress(0);
        },
        onError: () => {
            setUploadProgress(0);
        },
    });

    return { ...mutation, uploadProgress };
}

export function useChangeInsuranceProvider(noteId: string) {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IChangeInsurance
    >({
        mutationKey: ["change-insurance-provider"],
        mutationFn: (payload) => changeInsuranceProvider(noteId, payload),
    });
}

export function useChangeClaimVoidStatus() {
    interface IChangeVoidStatusResponse {
        voided_claims: string[];
        payer_voided_claims: Array<{
            claim_id: string;
            void_claim_id: string;
        }>;
        unvoided_claims: Array<{
            claim_id: string;
            reason: string;
        }>;
    }

    return useMutation<
        IApiResponse<IChangeVoidStatusResponse>,
        AxiosError<IApiResponse<IChangeVoidStatusResponse>>,
        IChangeClaimVoidStatus
    >({
        mutationKey: ["void-claim"],
        mutationFn: (data: IChangeClaimVoidStatus) =>
            changeClaimVoidStatus(data),
    });
}

export function useUpdateClaimsActivity() {
    return useMutation<
        IApiResponse<IClaimActivity>,
        AxiosError<IApiResponse<IClaimActivity>>,
        IUpdateClaimsActivity
    >({
        mutationKey: ["update-claim-activity"],
        mutationFn: (data: IUpdateClaimsActivity) => updateClaimsActivity(data),
    });
}

export function useChangeClaimsProvider() {
    return useMutation<
        IApiResponse<IClaim>,
        AxiosError<IApiResponse<IClaim>>,
        IChangeClaimsProvider
    >({
        mutationKey: ["change-claims-provider"],
        mutationFn: (data: IChangeClaimsProvider) => changeClaimsProvider(data),
    });
}
